
import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import UniversalPlan from "./UniversalPlan";
import FAQSection from "./FAQSection";
import { useToast } from "@/hooks/use-toast";
import { userApiClient, businessApiClient, subscriptionApiClient } from "@/api";
import { faqs } from "./data";

const PlansPageLayout: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        setIsLoading(true);
        
        const userId = await userApiClient.getCurrentUser();
        if (!userId) {
          toast({
            title: "Authentication required",
            description: "Please sign in to view subscription plans.",
            variant: "destructive",
          });
          return;
        }
        
        const business = await businessApiClient.fetchBusinessByOwnerId(userId);
        if (!business) {
          return;
        }
        
        setBusinessId(business.id);
        
        // Check subscription status
        const subscriptionDetails = await businessApiClient.fetchSubscriptionDetails(business.id);
        setHasActiveSubscription(subscriptionDetails?.status === 'active');
      } catch (error) {
        console.error("Error checking subscription:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSubscriptionStatus();
  }, [toast]);

  const handleSubscribe = async () => {
    if (!businessId) {
      toast({
        title: "Error",
        description: "Business profile not found. Please try again.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsCheckoutLoading(true);
      
      toast({
        title: "Processing payment",
        description: "Preparing your checkout session...",
      });

      // Get user data
      const userId = await userApiClient.getCurrentUser();
      
      if (!userId) {
        throw new Error("You must be logged in to complete this action");
      }

      // Create Stripe checkout session
      const data = await subscriptionApiClient.initiateCheckout(
        userId,
        businessId,
        "price_universal_subscription"
      );

      // Redirect to Stripe checkout
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Failed to create checkout session");
      }
      
    } catch (error: any) {
      console.error("Error initiating payment:", error);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to process payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCheckoutLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-6xl">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Universal Subscription</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Get unlimited access to all AI staff with our simple subscription plan.
        </p>
      </div>
      
      <div className="my-12 max-w-lg mx-auto">
        <UniversalPlan 
          hasActiveSubscription={hasActiveSubscription} 
          onSubscribe={handleSubscribe}
          isCheckoutLoading={isCheckoutLoading}
        />
      </div>
      
      <div className="mt-24">
        <FAQSection faqs={faqs} />
      </div>
    </div>
  );
};

export default PlansPageLayout;
